import { useEffect, useState } from 'react';
import { useFeedProvenance } from '@lemontaigu/staff/react';
import { feedStamp, type FeedProvenanceView } from './logic.js';

// « importé à … » — the line that stops a stopped feed from hiding.
//
// RUNBOOK §20.7, written before M4 and aimed straight at it:
//
//   "M4 makes `board_days` read the feed, and Vega-owned inputs become
//    read-only. At that point a stale feed is *invisible wrongness* — the exact
//    failure the brief set out to end. […] A feed nobody can tell has stopped
//    is a manual entry nobody remembers to do, wearing a better disguise."
//
// Before M4 an un-entered board was obvious: the fields were blank and somebody
// had to type them. After M4 the numbers always render, and only a timestamp
// separates a feed that ran a minute ago from one that died last Tuesday.
//
// Every decision — fresh vs stale, relative vs clock time, "never ran" vs
// "could not find out" — is in `feedStamp()` in `logic.ts`, pure and tested.
// This component is the fetch and the markup, nothing more.

export function FeedStamp() {
  const provenance = useFeedProvenance();
  const [state, setState] = useState<FeedProvenanceView | null | 'unknown'>('unknown');

  useEffect(() => {
    if (!provenance) return;
    let alive = true;
    void (async () => {
      try {
        const r = await provenance.latest();
        if (alive) setState(r);
      } catch {
        // A provenance read that fails must not take the board down with it —
        // the figures are still worth showing. It degrades to « inconnu », which
        // is not the same as fresh and does not render like it.
        if (alive) setState('unknown');
      }
    })();
    return () => {
      alive = false;
    };
  }, [provenance]);

  const { text, tone, title } = feedStamp(state, Date.now());
  return (
    <span className={`board-feed-stamp is-${tone}`} title={title}>
      {text}
    </span>
  );
}
